import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useDrawers } from "@/hooks/useDrawers";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Clock, Ruler, Quote, Package, Flower2, Palette } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import { useToast } from "@/hooks/use-toast";
import type { Flower } from "@/hooks/useFlowersData";

export default function FlowerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { openRFQ } = useDrawers();
  const { toast } = useToast();
  const [flower, setFlower] = useState<Flower | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      navigate("/catalog");
      return;
    }

    const fetchFlower = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const { data, error: supabaseError } = await supabase
          .from('flowers')
          .select('*')
          .eq('id', id)
          .maybeSingle();

        if (supabaseError) {
          throw supabaseError;
        }

        if (!data) {
          setError("Flower not found");
          return;
        }

        setFlower(data);
      } catch (err) {
        console.error('Error fetching flower:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch flower details';
        setError(errorMessage);
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchFlower();
  }, [id, navigate, toast]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-20">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading flower details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !flower) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-20">
          <div className="text-center">
            <h1 className="text-2xl font-light text-foreground mb-4">
              {error || "Flower not found"}
            </h1>
            <Link to="/catalog">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Catalog
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title={`${flower.name} - ${flower.color} | Flower Catalog`}
        description={`Premium ${flower.color} ${flower.name} from Ecuador. ${flower.stem_length_range ? `Stem length: ${flower.stem_length_range}. ` : ''}${flower.duration_range ? `Vase life: ${flower.duration_range}.` : ''}`}
      />

      {/* Hero Section */}
      <section className="py-12 bg-gradient-andean text-andean-cream">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-4 mb-6">
            <Link to="/catalog">
              <Button variant="ghost" size="sm" className="text-andean-cream hover:bg-andean-cream/10">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Catalog
              </Button>
            </Link>
          </div>
          
          <div className="max-w-4xl">
            <h1 className="text-3xl md:text-4xl font-display font-light mb-4 tracking-wide">
              {flower.name}
            </h1>
          </div>
        </div>
      </section>

      {/* Flower Details */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Image */}
            <div className="space-y-6">
              <Card className="overflow-hidden bg-card border-border">
                <div className="aspect-square relative bg-muted">
                  {flower.image_url ? (
                    <img
                      src={flower.image_url}
                      alt={`${flower.name} - ${flower.color}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <div className="text-center">
                        <div className="text-8xl mb-4">🌸</div>
                        <p className="text-lg">No image available</p>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* Details */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-display font-light text-foreground mb-6">
                  Flower Details
                </h2>
                
                <div className="space-y-4">
                  <Card className="bg-card border-border">
                    <CardContent className="p-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {flower.type && (
                          <div className="flex items-start gap-3">
                            <Flower2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                            <div>
                              <h4 className="font-medium text-foreground">Type</h4>
                              <p className="text-muted-foreground">{flower.type}</p>
                            </div>
                          </div>
                        )}

                        <div className="flex items-start gap-3">
                          <Palette className="h-5 w-5 text-muted-foreground mt-0.5" />
                          <div>
                            <h4 className="font-medium text-foreground">Color</h4>
                            <p className="text-muted-foreground capitalize">{flower.color}</p>
                          </div>
                        </div>

                        {flower.stem_length_range && (
                          <div className="flex items-start gap-3">
                            <Ruler className="h-5 w-5 text-muted-foreground mt-0.5" />
                            <div>
                              <h4 className="font-medium text-foreground">Stem Length</h4>
                              <p className="text-muted-foreground">{flower.stem_length_range}</p>
                            </div>
                          </div>
                        )}
                        
                        {flower.duration_range && (
                          <div className="flex items-start gap-3">
                            <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                            <div>
                              <h4 className="font-medium text-foreground">Vase Life</h4>
                              <p className="text-muted-foreground">{flower.duration_range}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
}